import { X } from "lucide-react";
import { Card } from "../../ui";
import { DateInput } from "../../DateInput";
import { formatCpfCnpj } from "../../../lib/masks";
import { StatusBadge } from "./shared";

export function LoteRowsTable({ associados, associadosSorted, busy, canSubmit, clearAll, done, removeRow, rows, setRowField, submit, summary }) {
  return (
    <>
      {rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-200 bg-slate-50">
            <div className="text-sm text-slate-700">
              <strong>{summary.total}</strong> arquivo(s) ·{" "}
              <span className={summary.assigned === summary.total ? "text-emerald-700" : "text-amber-700"}>
                {summary.assigned}/{summary.total} vinculado(s)
              </span>
              {done && (
                <>
                  {" · "}
                  <span className="text-emerald-700">{summary.ok} ok</span>
                  {" · "}
                  <span className="text-rose-700">{summary.err} erro(s)</span>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={clearAll}
                disabled={busy}
                className="text-[11px] uppercase tracking-[0.22em] px-3 py-2 border border-slate-300 hover:border-slate-400 disabled:opacity-50"
              >
                Limpar
              </button>
              <button
                data-testid="lote-submit"
                onClick={submit}
                disabled={!canSubmit}
                className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 bg-[#0A0F14] text-white hover:bg-[#002776] disabled:opacity-50"
              >
                {busy ? "Enviando..." : `Enviar ${rows.filter((r) => r.status !== "ok").length} boleto(s)`}
              </button>
            </div>
          </div>

          <ul className="divide-y divide-slate-200">
            {rows.map((row) => (
              <li
                key={row.id}
                data-testid={`lote-row-${row.id}`}
                className="p-4 grid md:grid-cols-[1fr_1fr_180px_auto] gap-3 items-start"
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Arquivo
                  </div>
                  <div className="text-sm font-medium truncate" title={row.file.name}>
                    {row.file.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {(row.file.size / 1024).toFixed(1)} KB
                  </div>
                  <input
                    data-testid={`lote-row-title-${row.id}`}
                    value={row.title}
                    onChange={(e) => setRowField(row.id, "title", e.target.value)}
                    placeholder="Título específico (opcional)"
                    className="mt-2 w-full border border-slate-200 px-2 py-1.5 rounded-sm text-xs"
                  />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Associado destinatário
                  </div>
                  <select
                    data-testid={`lote-row-associate-${row.id}`}
                    value={row.associate_id}
                    onChange={(e) =>
                      setRowField(row.id, "associate_id", e.target.value)
                    }
                    className={`mt-1 w-full border px-2 py-2 rounded-sm text-sm bg-white ${
                      row.associate_id
                        ? "border-slate-300"
                        : "border-amber-400 bg-amber-50"
                    }`}
                    disabled={busy || row.status === "ok"}
                  >
                    <option value="">Selecione o associado…</option>
                    {associadosSorted.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                        {a.cpf_cnpj ? ` · ${formatCpfCnpj(a.cpf_cnpj)}` : ""}
                      </option>
                    ))}
                  </select>
                  {row.associate_id && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      E-mail:{" "}
                      {(() => {
                        const a = associados.find((x) => x.id === row.associate_id);
                        return a
                          ? a.boleto_email ||
                              a.financial_contact_email ||
                              a.email ||
                              "sem e-mail cadastrado"
                          : "";
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Valor (R$)
                  </div>
                  <input
                    data-testid={`lote-row-valor-${row.id}`}
                    type="text"
                    inputMode="decimal"
                    placeholder="250,00"
                    value={row.valor || ""}
                    onChange={(e) => setRowField(row.id, "valor", e.target.value)}
                    disabled={busy || row.status === "ok"}
                    className={`mt-1 w-full border px-2 py-2 rounded-sm text-sm ${
                      row.valor ? "border-slate-300" : "border-amber-400 bg-amber-50"
                    }`}
                  />
                  <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Vencimento
                  </div>
                  <DateInput
                    testid={`lote-row-due-date-${row.id}`}
                    value={row.due_date || ""}
                    onChange={(v) => setRowField(row.id, "due_date", v)}
                    disabled={busy || row.status === "ok"}
                    compact
                  />
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={row.status} error={row.error} emailStatus={row.email_status} />
                  <button
                    data-testid={`lote-row-remove-${row.id}`}
                    onClick={() => removeRow(row.id)}
                    disabled={busy}
                    className="p-2 border border-slate-300 hover:border-rose-400 hover:text-rose-600 rounded-sm disabled:opacity-50"
                    title="Remover"
                  >
                    <X size={14} />
                  </button>
                </div>
                {row.status === "error" && (
                  <div className="md:col-span-4 -mt-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2">
                    {row.error}
                  </div>
                )}
                {row.status === "ok" && row.email_status && (
                  <div
                    data-testid={`row-email-status-${row.id}`}
                    className={`md:col-span-4 -mt-1 text-xs rounded-sm px-3 py-2 ${
                      row.email_status.sent > 0
                        ? "text-emerald-800 bg-emerald-50 border border-emerald-200"
                        : "text-amber-800 bg-amber-50 border border-amber-200"
                    }`}
                  >
                    {row.email_status.sent > 0 ? (
                      <>
                        E-mail enviado para{" "}
                        <strong>
                          {(row.email_status.delivered_to || []).join(", ")}
                        </strong>
                        {row.email_status.failed > 0 &&
                          ` · ${row.email_status.failed} falha(s)`}
                      </>
                    ) : row.email_status.skipped_no_email > 0 ? (
                      <>
                        Boleto salvo, porém o associado não possui e-mail
                        cadastrado. Cadastre um e-mail e reenvie manualmente.
                      </>
                    ) : row.email_status.failed > 0 ? (
                      <>
                        Boleto salvo, mas o envio de e-mail falhou. Verifique a
                        caixa do destinatário ou a configuração SMTP.
                      </>
                    ) : (
                      <>
                        Boleto salvo. Envio de e-mail estava desativado neste
                        upload.
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
